/**
 * WaitTimeReportPrompt — dashboard tile that lets a student contribute
 * their appointment wait to the crowd-sourced pool. Only appears when the
 * backend says the caller is eligible (case in visa_applied/visa_approved,
 * hasn't reported yet, hasn't dismissed).
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Clock3, X } from "lucide-react";

export default function WaitTimeReportPrompt() {
  const [state, setState] = useState(null); // null=loading, {eligible, country}
  const [days, setDays] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/wait-times/me/prompt-state")
      .then(r => setState(r.data))
      .catch(() => setState({ eligible: false }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const n = parseInt(days, 10);
    if (!Number.isFinite(n) || n < 0 || n > 365) {
      setErr("Enter a number between 0 and 365.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/wait-times/report", {
        days_to_appointment: n,
        city: city.trim() || null,
      });
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    try { await api.post("/wait-times/me/dismiss"); } catch {}
    setState({ eligible: false });
  };

  if (!state?.eligible && !done) return null;

  return (
    <div
      data-testid="wait-time-report-prompt"
      className="mt-6 moe-card p-5 md:p-6 border-2 border-elec/30 bg-[color:var(--moe-mint)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <Clock3 size={16} className="text-elec shrink-0 mt-0.5" strokeWidth={2.4}/>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-widest text-elec font-bold">Help future students</div>
            <div className="mt-1 text-base font-bold text-ink leading-snug">
              How long did your visa appointment take to get booked?
            </div>
            <div className="mt-1 text-[12px] text-muted leading-snug">
              From the day you first tried to book, to the actual appointment date.
              Your report stays anonymous. Only the aggregate shows on the country page.
            </div>
          </div>
        </div>
        {!done && (
          <button
            type="button"
            onClick={dismiss}
            data-testid="wait-time-dismiss"
            className="text-muted hover:text-ink transition-colors shrink-0"
            aria-label="Dismiss"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {done ? (
        <div
          data-testid="wait-time-report-success"
          className="mt-4 p-3 rounded-lg bg-white text-sm text-ink"
        >
          Reported. Thank you. Your data will appear in the aggregate once
          this consulate reaches 3 reports.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-4 grid sm:grid-cols-[1fr_1.5fr_auto] gap-2.5">
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Days</span>
            <input
              data-testid="wait-time-days-input"
              type="number"
              min={0}
              max={365}
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="mt-1 h-11 px-3 rounded-lg border border-ink/20 bg-white text-sm focus:outline-none focus:border-elec"
              placeholder="e.g. 21"
              required
            />
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] uppercase tracking-widest text-muted font-bold">Consulate city (optional)</span>
            <input
              data-testid="wait-time-city-input"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="mt-1 h-11 px-3 rounded-lg border border-ink/20 bg-white text-sm focus:outline-none focus:border-elec"
              placeholder="e.g. Mumbai, Delhi, Bangalore"
              maxLength={80}
            />
          </label>
          <button
            type="submit"
            data-testid="wait-time-submit"
            disabled={busy}
            className="self-end h-11 px-5 rounded-lg bg-ink text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Sending…" : "Report"}
          </button>
          {err && (
            <div data-testid="wait-time-error" className="sm:col-span-3 text-[12px] text-[color:var(--moe-pink)]">
              {err}
            </div>
          )}
        </form>
      )}
    </div>
  );
}
